export type LayoutInputPhoto = {
  id: string
  aspectRatio: number
}

export type CollageRow = {
  photoIds: string[]
  /** The one gap value used for every slot in this row — before the first photo, between each
   * pair, and after the last — so spacing is always uniform within a row. Rendered with the row
   * centered (`justify-content: center`) using this as the flex `gap`. Normally exactly the base
   * `gapPx`, since every row's height is solved to make it fill the available width exactly at that
   * spacing; only widened when a row's height had to be safety-clamped (see `computeCollageLayout`),
   * leaving genuine slack to redistribute. */
  gapPx: number
  /** This row's own height — independent of every other row's, unlike a classic uniform grid. Each
   * row solves for whatever height makes its own photos exactly fill the available width (see
   * `idealRowHeight`), so a row stuck with an awkward mix of aspect ratios doesn't just settle for
   * the least-bad leftover — it's never under-filled at all. */
  rowHeightPx: number
}

export type CollageLayout = {
  rows: CollageRow[]
  verticalGapPx: number
}

const MIN_ROW_HEIGHT = 4
/** Above this many photos, sample row-count candidates instead of trying every one, so the search
 * (whose cost per candidate grows with both row count and photo count) stays fast for large
 * collages. */
const MAX_ROW_COUNT_CANDIDATES = 40
/** Safety valve for a photo (or the whole collage) with such an extreme aspect ratio that solving
 * its row height to exactly fill the width would need an absurd height (see `computeCollageLayout`
 * doc). Caps a row at this multiple of the available width. */
const MAX_ROW_HEIGHT_MULTIPLE = 3

/** The height that makes a row of photos with this combined aspect ratio exactly fill the
 * available width, spaced at exactly `gapPx` in every slot (edges included). Because this is
 * solved directly from the width constraint, it can never overflow — unlike sharing one height
 * across every row, there's no "does this fit" question to get wrong. */
function idealRowHeight(aspectSum: number, photoCount: number, availableWidth: number, gapPx: number): number {
  const slotCount = photoCount + 1
  return Math.max(MIN_ROW_HEIGHT, (availableWidth - slotCount * gapPx) / aspectSum)
}

type Grouping = { indexGroups: number[][]; heights: number[]; totalHeight: number; aspectRatio: number }

/**
 * Partitions photos into exactly `rowCount` contiguous groups (row order follows photo order),
 * choosing breakpoints to keep every row's independently-solved height as close as possible to
 * `target` — a 2-D dynamic program (row-count is now a hard constraint, not just a cost) over "use
 * r groups for the first `end` photos." Returns null if `rowCount` photos can't be split this way
 * (e.g. more rows requested than photos, or gaps alone would exceed the width for some group).
 */
function breakIntoExactly(
  photos: LayoutInputPhoto[],
  availableWidth: number,
  gapPx: number,
  target: number,
  rowCount: number,
): number[][] | null {
  const n = photos.length
  if (rowCount > n || rowCount < 1) return null

  const prefixAspect = new Array<number>(n + 1).fill(0)
  for (let i = 0; i < n; i++) prefixAspect[i + 1] = prefixAspect[i] + photos[i].aspectRatio

  const dp: number[][] = Array.from({ length: rowCount + 1 }, () => new Array<number>(n + 1).fill(Infinity))
  const at: number[][] = Array.from({ length: rowCount + 1 }, () => new Array<number>(n + 1).fill(-1))
  dp[0][0] = 0

  for (let r = 1; r <= rowCount; r++) {
    for (let end = r; end <= n - (rowCount - r); end++) {
      for (let start = end - 1; start >= r - 1; start--) {
        if (dp[r - 1][start] === Infinity) continue
        const groupSize = end - start
        const slotCount = groupSize + 1
        if (slotCount * gapPx >= availableWidth) break // only gets worse as the group grows further

        const aspectSum = prefixAspect[end] - prefixAspect[start]
        const h = idealRowHeight(aspectSum, groupSize, availableWidth, gapPx)
        const cost = dp[r - 1][start] + (h - target) ** 2
        if (cost < dp[r][end]) {
          dp[r][end] = cost
          at[r][end] = start
        }
      }
    }
  }

  if (dp[rowCount][n] === Infinity) return null

  const groups: number[][] = []
  let end = n
  let r = rowCount
  while (r > 0) {
    const start = at[r][end]
    groups.unshift(Array.from({ length: end - start }, (_, i) => start + i))
    end = start
    r--
  }
  return groups
}

function evaluateRowCount(
  photos: LayoutInputPhoto[],
  availableWidth: number,
  gapPx: number,
  rowCount: number,
  verticalBorderPx: number,
): Grouping | null {
  const target = availableWidth / rowCount
  const indexGroups = breakIntoExactly(photos, availableWidth, gapPx, target, rowCount)
  if (!indexGroups) return null

  const heights = indexGroups.map((group) => {
    const aspectSum = group.reduce((sum, i) => sum + photos[i].aspectRatio, 0)
    return idealRowHeight(aspectSum, group.length, availableWidth, gapPx)
  })
  const totalHeight = heights.reduce((a, b) => a + b, 0) + Math.max(0, indexGroups.length - 1) * gapPx
  // The bounding box being judged for squareness is the outer, bordered one: width already has its
  // border built into each row's own edge slots (see `idealRowHeight`), but height additionally
  // gets a top-and-bottom border from the container's own padding (see `computeCollageLayout`),
  // which doesn't fall out of the row math and so has to be added on here explicitly.
  const outerHeight = totalHeight + verticalBorderPx
  const aspectRatio = outerHeight > 0 ? availableWidth / outerHeight : 1

  return { indexGroups, heights, totalHeight, aspectRatio }
}

/**
 * Tries every achievable row count (sampled for very large collages) and picks whichever comes
 * closest to a square bounding box. Every row count's grouping already fills its rows' full width
 * exactly (see `idealRowHeight`), so — unlike sharing one height across every row — there's no
 * "which grouping wastes least space" question left to answer; squareness is the only thing left
 * to optimize for.
 */
function findBestGrouping(
  photos: LayoutInputPhoto[],
  availableWidth: number,
  gapPx: number,
  verticalBorderPx: number,
): Grouping {
  const n = photos.length
  const rowCountCandidates: number[] = []
  if (n <= MAX_ROW_COUNT_CANDIDATES) {
    for (let r = 1; r <= n; r++) rowCountCandidates.push(r)
  } else {
    for (let i = 0; i < MAX_ROW_COUNT_CANDIDATES; i++) {
      rowCountCandidates.push(1 + Math.round((i * (n - 1)) / (MAX_ROW_COUNT_CANDIDATES - 1)))
    }
  }

  let best: Grouping | null = null
  for (const rowCount of rowCountCandidates) {
    const candidate = evaluateRowCount(photos, availableWidth, gapPx, rowCount, verticalBorderPx)
    if (!candidate) continue
    if (!best || Math.abs(Math.log(candidate.aspectRatio)) < Math.abs(Math.log(best.aspectRatio))) {
      best = candidate
    }
  }

  return (
    best ??
    evaluateRowCount(photos, availableWidth, gapPx, n, verticalBorderPx) ?? {
      indexGroups: [],
      heights: [],
      totalHeight: 0,
      aspectRatio: 1,
    }
  )
}

/**
 * Packs photos into rows, each independently sized so its own photos exactly fill the available
 * width at the base spacing (every slot — edges and between-photo gaps alike — comes out to exactly
 * `gapPx`, with no leftover to redistribute). This is the classic justified-gallery technique: since
 * every row already uses its width perfectly regardless of which photos land in it, the only
 * remaining choice is which row count makes the whole bordered collage closest to square (see
 * `findBestGrouping`) — there's no "least-wasteful among imperfect options" tradeoff left to make,
 * unlike sharing one row height across the whole collage (which this replaced: a row stuck with an
 * awkward mix of aspect ratios used to settle for the least-bad leftover; now it is never
 * under-filled at all).
 *
 * One case doesn't fit "solve this row's height from its own photos" at all: a photo (or the whole
 * collage) with such an extreme aspect ratio that filling the width would need an absurd height —
 * clearest with a single very tall, very narrow photo alone. That row's height is capped, trading
 * a perfectly filled row for a reasonable one; the leftover it then has is spread evenly across the
 * row's slots exactly like every row was before this rewrite.
 *
 * `containerWidth` is the full outer width, border included. Unlike an earlier version of this
 * function, it is *not* pre-shrunk by the border here: each row's own edge slots (see
 * `idealRowHeight`) already reserve exactly `gapPx` on the left and right when a row is fully
 * packed, so a second, separate reservation at the container level double-counted that border —
 * a photo ended up `gapPx` further from the edge than from its row neighbor, instead of the same
 * distance. The left/right border is entirely a side effect of each row's own slot math now. Only
 * the top-and-bottom border still has nothing else providing it, so the caller (`CollageGrid`)
 * applies that directly as vertical-only CSS padding, and `findBestGrouping` accounts for it when
 * judging squareness. Row order follows input order, so reordering photos reorders the packing.
 */
export function computeCollageLayout(photos: LayoutInputPhoto[], containerWidth: number, gapPx: number): CollageLayout {
  const availableWidth = containerWidth
  if (photos.length === 0 || availableWidth <= 0) {
    return { rows: [], verticalGapPx: gapPx }
  }

  let effectiveGapPx = gapPx
  let { indexGroups, heights } = findBestGrouping(photos, availableWidth, effectiveGapPx, 2 * effectiveGapPx)

  if (indexGroups.length === 0) {
    // gapPx is so large relative to availableWidth that not even one photo per row fits (each row
    // needs slotCount * gapPx < availableWidth — see idealRowHeight). Rather than silently
    // rendering nothing, shrink the gap just enough to guarantee every photo gets its own row: a
    // graceful, if cramped, degradation instead of the whole collage going blank. This only kicks
    // in for a narrow container combined with a large spacing value; normal use never reaches it.
    effectiveGapPx = Math.max(0, Math.min(gapPx, availableWidth / 4))
    ;({ indexGroups, heights } = findBestGrouping(photos, availableWidth, effectiveGapPx, 2 * effectiveGapPx))
  }

  const maxRowHeight = availableWidth * MAX_ROW_HEIGHT_MULTIPLE

  const rows: CollageRow[] = indexGroups.map((group, i) => {
    const photoIds = group.map((idx) => photos[idx].id)
    const naturalHeight = heights[i]
    if (naturalHeight <= maxRowHeight) {
      return { photoIds, gapPx: effectiveGapPx, rowHeightPx: naturalHeight }
    }
    // Extreme-outlier safety valve: this row's photos would need an absurd height to exactly fill
    // the width, so cap it and spread the resulting (now genuine) leftover evenly across its slots.
    const rowHeightPx = maxRowHeight
    const aspectSum = group.reduce((sum, idx) => sum + photos[idx].aspectRatio, 0)
    const naturalWidth = aspectSum * rowHeightPx
    const slotCount = group.length + 1
    const leftover = Math.max(0, availableWidth - naturalWidth - slotCount * effectiveGapPx)
    return { photoIds, gapPx: effectiveGapPx + leftover / slotCount, rowHeightPx }
  })

  return { rows, verticalGapPx: effectiveGapPx }
}
